import {clearAllBodyScrollLocks} from 'body-scroll-lock';
import * as React from 'react';
import {useEffect, useMemo} from 'react';

import {RunQueue} from '../generated/graphql';
import * as CustomRunColorsTypes from '../state/views/customRunColors/types';
import {EMPTY_CUSTOM_RUN_COLORS} from '../state/views/customRunColors/util';
import * as CustomRunNamesTypes from '../state/views/customRunNames/types';
import {EMPTY_CUSTOM_RUN_NAMES} from '../state/views/customRunNames/util';
import * as ViewHooks from '../state/views/hooks';
import * as RunSetTypes from '../state/views/runSet/types';
import * as SemanticLegendTypes from '../state/views/semanticLegend/types';
import * as TempSelectionsTypes from '../state/views/tempSelections/types';
import {BenchmarkProject} from '../types/graphql';
import {Grouping} from '../util/queryTypes';
import {emptyRunSetSelectAll} from '../util/section';
import * as SM from '../util/selectionmanager';
import {LoaderProxy} from './../containers/AppLoader';
import {ErrorBoundary} from './ErrorBoundary';
import {RunSelectorAnalyticsLocation} from './RunSelector/RunSelectorAnalyticsLocationContext';
import {RunSelectorInner} from './RunSelector/RunSelectorInner';
import {DisabledFeaturesMap} from './RunSelector/types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from './WorkspaceDrawer/Settings/semanticLegend/SemanticLegendDefaults';
/*
 * RunSelector is WBTable, wrapped with RunSelectorQuery (to fetch tags)
 * And n RunsDataLoader queries (one for the top level query, and one for each expanded group)
 */

interface RunSelectorProps {
  analyticsLocation: RunSelectorAnalyticsLocation;
  benchmarkMeta?: BenchmarkProject;
  className?: string;
  customRunColorsRef?: CustomRunColorsTypes.Ref;
  customRunNamesRef?: CustomRunNamesTypes.Ref;
  displayBenchmark?: boolean;
  enableFreezeRunset?: boolean;
  enableSetProject?: boolean;
  //  isExpanded/isExpandable on the RunSelector is a very weird pair of
  //  properties. If both are true, it renders as the full-width run selector.
  //  If both are undefined, it renders as a reports-style inline table.
  //  If isExpanded is false, but isExpandable is true, it renders only
  //  the pinned columns. If isExpandable is false, but isExpanded is true,
  //  I think that might be undefined behavior. -- NH
  isExpandable?: boolean;
  isExpanded?: boolean;
  isSingleMode?: boolean;
  onSelectRunName?: (runId: string) => void;
  recommendedGrouping?: Grouping;
  runSetRef?: RunSetTypes.Ref;
  selectedRunName?: string;
  semanticLegendRef?: SemanticLegendTypes.Ref;
  tempSelectionsRef?: TempSelectionsTypes.Ref;
  title?: string | React.ReactElement;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;
  onSetExpanded?: (expanded: boolean) => void;
  disabledFeatures?: DisabledFeaturesMap;
  disableRunLinks?: boolean;
  runQueues?: RunQueue[];
}

const RunSelector = (props: RunSelectorProps) => {
  useEffect(() => {
    return () => clearAllBodyScrollLocks();
  }, []);

  const customRunColorsRef = ViewHooks.useExistingOrNewObjectRef(
    'run-colors',
    EMPTY_CUSTOM_RUN_COLORS,
    props.customRunColorsRef
  );
  const customRunNamesRef = ViewHooks.useExistingOrNewObjectRef(
    'run-names',
    EMPTY_CUSTOM_RUN_NAMES,
    props.customRunNamesRef
  );

  const semanticLegendRef = ViewHooks.useExistingOrNewObjectRef(
    'semantic-legend',
    DEFAULT_SEMANTIC_LEGEND_SETTINGS,
    props.semanticLegendRef
  );

  const emptyRunSet = useMemo(emptyRunSetSelectAll, []);
  const runSetRef = ViewHooks.useExistingOrNewObjectRef(
    'runSet',
    emptyRunSet,
    props.runSetRef
  );

  const tempSelectionsRef = ViewHooks.useExistingOrNewObjectRef(
    'temp-selections',
    SM.EMPTY_NONE_SELECTIONS,
    props.tempSelectionsRef
  );

  if (
    !customRunColorsRef.ready ||
    !customRunNamesRef.ready ||
    !runSetRef.ready ||
    !semanticLegendRef.ready ||
    !tempSelectionsRef.ready
  ) {
    return <LoaderProxy name="run-selector-loader" />;
  }

  const refProps = {
    customRunColorsRef: customRunColorsRef.ref,
    customRunNamesRef: customRunNamesRef.ref,
    runSetRef: runSetRef.ref,
    semanticLegendRef: semanticLegendRef.ref,
    tempSelectionsRef: tempSelectionsRef.ref,
  };

  return (
    <ErrorBoundary renderError={TempErrorBoundary}>
      <RunSelectorInner {...props} {...refProps} />
    </ErrorBoundary>
  );
};

export default RunSelector;

function TempErrorBoundary() {
  console.error('Error loading runs, likely due to a permissions issue.');
  return <></>;
}
